import type { AssignScopeInput, Scope, Site } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, ErrorText } from '../../ui';

/** Sedi dell'azienda (ADR 0017, 0141): solo le certe sono un ambito; con una sola sede l'ambito non si mostra. */
export function useSites() {
  const q = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const open = (q.data?.data ?? []).filter((s) => s.status === 'attiva');
  return { ...q, sites: open, multi: open.length > 1, name: (id: string) => q.data?.data.find((s) => s.id === id)?.name ?? 'sede tolta' };
}

/** Per chi vale: tutta l'azienda, sedi precise o da attribuire; "dal sistema" quando non l'ha deciso la persona. */
export function ScopeBadge({ ambito }: { ambito: Scope | undefined }) {
  const { multi, name } = useSites();
  if (!multi || !ambito) return null;
  const label = ambito.ambito === 'azienda' ? 'Tutta l’azienda' : ambito.ambito === 'sedi' ? ambito.sedi.map(name).join(', ') : 'Sede da attribuire';
  const tone = ambito.ambito === 'da_attribuire' ? 'amber' : ambito.ambito === 'sedi' ? 'blue' : 'gray';
  return (
    <span title={ambito.motivo ?? undefined}>
      <Badge tone={tone}>
        {label}
        {ambito.fonte !== 'persona' && ambito.ambito !== 'da_attribuire' ? (ambito.fonte === 'regola' ? ' · da regola' : ' · dal sistema') : ''}
      </Badge>
    </span>
  );
}

/** La persona dice a quale sede si riferisce un elemento (o a tutta l'azienda): il sistema non lo cambia più. */
export function ScopeEditor({ tipo, id, ambito, onDone }: { tipo: AssignScopeInput['tipo']; id: string; ambito: Scope | undefined; onDone: () => void }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const { sites } = useSites();
  const [chosen, setChosen] = useState<string[]>(ambito?.ambito === 'sedi' ? ambito.sedi : []);
  const save = useMutation({
    mutationFn: () => api(t('/sites/assign'), { method: 'PUT', body: { tipo, id, ambito: chosen.length ? 'sedi' : 'azienda', sedi: chosen } }),
    onSuccess: () => {
      void qc.invalidateQueries();
      onDone();
    },
  });
  const toggle = (s: string) => setChosen(chosen.includes(s) ? chosen.filter((x) => x !== s) : [...chosen, s]);
  return (
    <div className="mt-2 w-full rounded-lg bg-black/[0.03] p-3 text-sm">
      <p className="mb-2 text-ink/70">Per quali sedi vale? Nessuna sede scelta = tutta l’azienda (vale a prescindere dalle sedi).</p>
      <div className="mb-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => setChosen([])} className={`rounded-full border px-3 py-1 text-xs ${!chosen.length ? 'border-accent bg-accent text-white' : 'border-black/15 text-ink/70'}`}>
          Tutta l’azienda
        </button>
        {sites.map((s) => (
          <button key={s.id} type="button" onClick={() => toggle(s.id)} className={`rounded-full border px-3 py-1 text-xs ${chosen.includes(s.id) ? 'border-accent bg-accent text-white' : 'border-black/15 text-ink/70'}`}>
            {s.name}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Salva
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </div>
  );
}

/** Badge dell'ambito con il pulsante per cambiarlo (solo responsabili e solo con più sedi). */
export function ScopeControl({ tipo, id, ambito }: { tipo: AssignScopeInput['tipo']; id: string; ambito: Scope | undefined }) {
  const { isManager } = useTenant();
  const { multi } = useSites();
  const [open, setOpen] = useState(false);
  if (!multi) return null;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <ScopeBadge ambito={ambito} />
        {isManager && (
          <button type="button" className="text-xs text-accent hover:underline" onClick={() => setOpen(!open)}>
            sede
          </button>
        )}
      </span>
      {open && <ScopeEditor tipo={tipo} id={id} ambito={ambito} onDone={() => setOpen(false)} />}
    </>
  );
}
